import React, { useContext, useMemo, useState, useEffect } from "react";

import GoogleMapReact from "google-map-react";
import MapItem from "./MapItem.jsx";
import { DealerContext } from "../../contexts/DealerContext.jsx";

import { calculate_distance, getMetersPerPx } from "./utils.js";

const Map = () => {

  const { dealers, loading, bounds, setBounds, zoom, apiKey } = useContext(DealerContext);

  // google-map-react assumes the API is ready as soon as it finds a script tag
  // with a matching id (our preload tag in main-dealer-locator.liquid), even if
  // it's still downloading. On slow connections that fires before google.maps
  // actually exists, so wait for our own load callback instead.
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));

  console.log('[dealer-locator DEBUG] Map render: mapsReady', mapsReady, 'loading', loading, 'window.google', !!window.google, 'window.google.maps', !!(window.google && window.google.maps), 'window.__dealerLocatorMapsReady', window.__dealerLocatorMapsReady, 'script tag', document.getElementById('__googleMapsScriptId'));

  useEffect(() => {
    if (mapsReady) {
      console.log('[dealer-locator DEBUG] mapsReady effect: already ready, skipping');
      return;
    }
    if (window.__dealerLocatorMapsReady) {
      console.log('[dealer-locator DEBUG] mapsReady effect: window.__dealerLocatorMapsReady already true, setting mapsReady');
      setMapsReady(true);
      return;
    }
    console.log('[dealer-locator DEBUG] mapsReady effect: not ready yet, listening for dealerLocator:mapsReady event');
    const onReady = () => {
      console.log('[dealer-locator DEBUG] dealerLocator:mapsReady event received, setting mapsReady');
      setMapsReady(true);
    };
    document.addEventListener('dealerLocator:mapsReady', onReady);
    return () => document.removeEventListener('dealerLocator:mapsReady', onReady);
  }, [mapsReady]);

  const getClusters = (zoom, dealers, center) => {
    const metersPerPx = getMetersPerPx(zoom, center);
    const clusters = [];
    for (var i = 0; i < dealers?.length; i++) {
      const dealer = dealers[i];
      let clustered = false;
      for (var j = 0; j < clusters.length; j++) {
        //file it in cluster if needed
        const cluster = clusters[j];
        const distance = calculate_distance(dealer, cluster.lat, cluster.lng);
        const distancePx = distance / metersPerPx;

        if (distancePx < 30) {
          clustered = true;
          cluster.dealers.push(dealer);
          cluster.indexes.push(i);
          cluster.ids.push(dealer.id);
          const latArray = cluster.dealers.map(dealer => parseFloat(dealer.lat));
          const lngArray = cluster.dealers.map(dealer => parseFloat(dealer.lng));
          cluster.lat = average(latArray);
          cluster.lng = average(lngArray);
          break;
        }
      }
      if (!clustered) {
        //add it to end of clusters
        clusters.push({
          lat: dealer.lat,
          lng: dealer.lng,
          dealers: [dealer],
          indexes: [i],
          ids: [dealer.id]
        })
      }
    }

    clusters.forEach(cluster => {
      cluster.dealers.sort((a, b) => {
        if(b.preferred_dealer || b.logo) return 1;
        return -1
      })
    });

    return clusters.sort((a, b) => {
      return b.lat - a.lat;
    });
  }

  const center = {
    lat: bounds.lat ? parseFloat(bounds.lat) : 43.7488057,
    lng: bounds.lng ? parseFloat(bounds.lng) : -114.3744295
  }

  const onBoundsChange = ({center, zoom}) => {
    if (!loading) {
      setBounds({
        ...bounds,
        lat: center.lat,
        lng: center.lng,
        zoom: zoom
      })
    }
  }

  const clusters = useMemo(() => getClusters(zoom, dealers, center), [zoom, dealers, center]);

  const mediaMatches = window.matchMedia("(max-width: 999px)");

  return (
    <div id="Map" className="ft_dealer-locator__results__map-container">
      <div className="ft_dealer-locator__results__map" style={{ height: "100%", width: "100%" }}>
          {!loading && mapsReady ? <MapErrorBoundary>
            <GoogleMapReact
              bootstrapURLKeys={{
                libraries: ['places'],
                key: apiKey,
                id: '__googleMapsScriptId'
              }}
              center={center}
              zoom={zoom || 9}
              hoverDistance={20}
              options={{
                gestureHandling: "greedy",
                fullscreenControl: false,
                zoomControl: false,
                clickableIcons: false
              }}
              onChange={onBoundsChange}
            >
              {
                clusters.map((cluster, index) => <MapItem
                  index={cluster.indexes[0] + 1}
                  key={`icon-${index}`}
                  dealers={cluster.dealers}
                  className="ft_dealer-locator__map__item"
                  center={center}
                  lat={cluster.lat}
                  lng={cluster.lng}
                  inContextResult={!mediaMatches.matches}
                />)
              }
            </GoogleMapReact>
          </MapErrorBoundary> : <span className="skeleton" style={{ width: "100%", height: "100%", borderRadius: 0, background: "white" }}></span>}
       </div>
    </div>
  );

}

export default Map;

// DEBUG: catches a GoogleMapReact crash and logs the google.maps state at the
// moment it happened, since the default uncaught-error trace doesn't show that.
class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[dealer-locator DEBUG] GoogleMapReact crashed:', error, info, {
      windowGoogle: window.google,
      windowGoogleMaps: window.google && window.google.maps,
      dealerLocatorMapsReady: window.__dealerLocatorMapsReady,
      scriptTag: document.getElementById('__googleMapsScriptId'),
    });
  }

  render() {
    if (this.state.hasError) {
      return <div className="ft_dealer-locator__map__error">Map failed to load. Check console for [dealer-locator DEBUG] logs.</div>;
    }
    return this.props.children;
  }
}


const average = (array) => array.reduce((a, b) => a + b) / array.length;
